/**
 * Fullscreen Mega Menu JavaScript
 * Handles hover interactions, keyboard navigation, and accessibility
 */

class FullscreenMegaMenu {
  constructor() {
    this.menuItems = document.querySelectorAll('[data-mega-menu-trigger]');
    this.activePanel = null;
    this.activeTrigger = null;
    this.activeItem = null;
    this.closeTimeout = null;
    this.openTimeout = null;
    this.openDelay = 100; // Delay before opening
    this.closeDelay = 150; // Delay before closing
    this.savedScrollY = 0;
    this.isMenuAreaHovered = false;
    
    this.init();
  }
  
  init() {
    if (this.menuItems.length === 0) return;
    
    // Only initialize on desktop
    if (window.innerWidth < 1025) return;
    
    this.menuItems.forEach(item => {
      const trigger = item.querySelector('.mega-menu-trigger');
      const panel = item.querySelector('[data-mega-menu-panel]');
      
      if (!trigger || !panel) return;
      
      // Mouse enter on trigger
      trigger.addEventListener('mouseenter', () => {
        this.isMenuAreaHovered = true;
        this.clearCloseTimeout();
        
        // If another panel is open, switch immediately
        if (this.activePanel && this.activePanel !== panel) {
          this.switchToPanel(item, trigger, panel);
        } else if (!this.activePanel) {
          // No panel open, open with delay
          this.clearOpenTimeout();
          this.openTimeout = setTimeout(() => {
            this.openPanel(item, trigger, panel);
          }, this.openDelay);
        }
      });
      
      // Mouse leave from trigger
      trigger.addEventListener('mouseleave', (e) => {
        this.clearOpenTimeout();
        
        // Check if moving to the panel
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && panel.contains(relatedTarget)) {
          return; // Moving to panel, don't close
        }
        
        this.scheduleClose();
      });
      
      // Mouse enter on panel
      panel.addEventListener('mouseenter', () => {
        this.isMenuAreaHovered = true;
        this.clearCloseTimeout();
      });
      
      // Mouse leave from panel
      panel.addEventListener('mouseleave', (e) => {
        // Check if moving to trigger or another menu item
        const relatedTarget = e.relatedTarget;
        if (relatedTarget) {
          // Check if moving to any trigger
          const movingToTrigger = relatedTarget.closest('[data-mega-menu-trigger]');
          if (movingToTrigger) {
            return; // Moving to another trigger, let that trigger handle it
          }
        }
        
        this.scheduleClose();
      });
      
      // Keyboard support
      trigger.addEventListener('keydown', (e) => {
        this.handleKeydown(e, item, trigger, panel);
      });
      
      // Click support
      trigger.addEventListener('click', (e) => {
        if (item.classList.contains('have-child')) {
          e.preventDefault();
          if (this.activePanel === panel) {
            this.closeAllPanels();
          } else {
            this.openPanel(item, trigger, panel);
          }
        }
      });
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activePanel) {
        this.closeAllPanels();
      }
    });
    
    // Close on click outside
    document.addEventListener('click', (e) => {
      if (this.activePanel && !e.target.closest('[data-mega-menu-trigger]') && !e.target.closest('[data-mega-menu-panel]')) {
        this.closeAllPanels();
      }
    });
    
    // Handle resize
    window.addEventListener('resize', this.debounce(() => {
      if (window.innerWidth < 1025 && this.activePanel) {
        this.closeAllPanels();
      }
    }, 100));
  }
  
  scheduleClose() {
    this.isMenuAreaHovered = false;
    this.clearCloseTimeout();
    
    this.closeTimeout = setTimeout(() => {
      if (!this.isMenuAreaHovered) {
        this.closeAllPanels();
      }
    }, this.closeDelay);
  }
  
  switchToPanel(item, trigger, panel) {
    // Close current panel visually (but keep body state)
    if (this.activePanel) {
      this.activePanel.classList.remove('is-open');
      if (this.activeTrigger) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }
    }
    
    // Calculate position for new panel
    const headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) {
      const headerRect = headerWrapper.getBoundingClientRect();
      panel.style.top = headerRect.bottom + 'px';
    }
    
    // Open new panel
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    this.activePanel = panel;
    this.activeTrigger = trigger;
    this.activeItem = item;
  }
  
  openPanel(item, trigger, panel) {
    // Save scroll position before opening
    if (!this.activePanel) {
      this.savedScrollY = window.scrollY;
    }
    
    // Calculate position
    const headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) {
      const headerRect = headerWrapper.getBoundingClientRect();
      panel.style.top = headerRect.bottom + 'px';
    }
    
    // Open panel
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    this.activePanel = panel;
    this.activeTrigger = trigger;
    this.activeItem = item;
    
    // Lock scroll
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.classList.add('mega-menu-open');
    
    // Trap focus
    this.trapFocus(panel);
  }
  
  closeAllPanels() {
    if (!this.activePanel) return;
    
    // Close all panels
    this.menuItems.forEach(item => {
      const trigger = item.querySelector('.mega-menu-trigger');
      const panel = item.querySelector('[data-mega-menu-panel]');
      
      if (panel) {
        panel.classList.remove('is-open');
      }
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
    
    // Restore scroll
    document.body.classList.remove('mega-menu-open');
    document.body.style.top = '';
    window.scrollTo(0, this.savedScrollY);
    
    // Reset state
    this.activePanel = null;
    this.activeTrigger = null;
    this.activeItem = null;
  }
  
  handleKeydown(e, item, trigger, panel) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        if (item.classList.contains('have-child')) {
          e.preventDefault();
          if (this.activePanel === panel) {
            this.closeAllPanels();
            trigger.focus();
          } else {
            this.openPanel(item, trigger, panel);
            const firstLink = panel.querySelector('a');
            if (firstLink) firstLink.focus();
          }
        }
        break;
        
      case 'ArrowDown':
        if (this.activePanel === panel) {
          e.preventDefault();
          const firstLink = panel.querySelector('a');
          if (firstLink) firstLink.focus();
        }
        break;
        
      case 'ArrowUp':
        if (this.activePanel === panel) {
          e.preventDefault();
          const links = panel.querySelectorAll('a');
          if (links.length) links[links.length - 1].focus();
        }
        break;
    }
  }
  
  trapFocus(panel) {
    const focusableElements = panel.querySelectorAll(
      'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    
    panel.addEventListener('keydown', handleTabKey);
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && !panel.classList.contains('is-open')) {
          panel.removeEventListener('keydown', handleTabKey);
          observer.disconnect();
        }
      });
    });
    
    observer.observe(panel, { attributes: true });
  }
  
  clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }
  
  clearOpenTimeout() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FullscreenMegaMenu();
  });
} else {
  new FullscreenMegaMenu();
}

// Re-initialize after Shopify section render (for theme editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.classList.contains('section-header')) {
    new FullscreenMegaMenu();
  }
});
